import { Index, Show, createSignal } from "solid-js";
import { api } from "../api/client";
import type { Exercise, ExerciseHistoryItem } from "../api/types";
import { t } from "../i18n";
import { lastTimeHint, rowType } from "../lib/activity";
import { loadCatalog } from "../lib/data";
import { catalogPatch, newExercise } from "../lib/exerciseRows";
import { createListDrag } from "../lib/listDrag";
import { moveItem, withSortOrder } from "../lib/reorder";
import ActivityFields from "./ActivityFields";
import DragHandle from "./DragHandle";
import ExerciseNameLookup from "./ExerciseNameLookup";
import MuscleGroupPicker from "./MuscleGroupPicker";

interface Props {
  exercises: Exercise[];
  onChange: (exercises: Exercise[]) => void;
  /** Highlight missing/invalid values (set after a failed save). */
  showErrors?: boolean;
}

/**
 * Use <Index> (not <For>): For keys rows by object identity, so replacing an
 * exercise on each keystroke remounts the input and steals focus.
 */
export default function ExerciseEditor(props: Props) {
  const [historyFor, setHistoryFor] = createSignal<number | null>(null);
  const [history, setHistory] = createSignal<ExerciseHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = createSignal(false);
  // "Last time" hints of non-strength rows, by lower-cased exercise name.
  const [hints, setHints] = createSignal<Record<string, string>>({});
  const hintOf = (ex: Exercise) =>
    rowType(ex) === "strength" ? "" : (hints()[ex.name.trim().toLowerCase()] ?? "");

  const drag = createListDrag({
    onReorder: (from, to) => {
      const next = withSortOrder(moveItem(props.exercises, from, to));
      // Keep history panel attached to the moved exercise when possible
      const hf = historyFor();
      if (hf != null) {
        if (hf === from) setHistoryFor(to);
        else if (from < hf && to >= hf) setHistoryFor(hf - 1);
        else if (from > hf && to <= hf) setHistoryFor(hf + 1);
      }
      props.onChange(next);
    },
  });

  function update(index: number, patch: Partial<Exercise>) {
    const list = props.exercises;
    const next = list.slice();
    next[index] = { ...list[index], ...patch };
    props.onChange(withSortOrder(next));
  }

  function remove(index: number) {
    props.onChange(
      withSortOrder(props.exercises.filter((_, i) => i !== index)),
    );
    if (historyFor() === index) {
      setHistoryFor(null);
      setHistory([]);
    } else if (historyFor() != null && historyFor()! > index) {
      setHistoryFor(historyFor()! - 1);
    }
  }

  function add() {
    props.onChange(
      withSortOrder([...props.exercises, newExercise()]),
    );
  }

  async function loadHistory(index: number) {
    const name = props.exercises[index]?.name?.trim();
    if (!name) return;
    const type = rowType(props.exercises[index]!);
    if (type !== "strength") {
      const key = name.toLowerCase();
      if (hints()[key] !== undefined) return;
      try {
        const hint = lastTimeHint(await api.exerciseHistory(name, 5), type);
        setHints((h) => ({ ...h, [key]: hint }));
      } catch {
        /* no hint */
      }
      return;
    }
    setHistoryFor(index);
    setHistoryLoading(true);
    try {
      const items = await api.exerciseHistory(name, 5);
      setHistory(items);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
    const current = props.exercises[index];
    if (!current?.muscleGroups?.length) {
      try {
        const catalog = await loadCatalog();
        const exact = catalog.find(
          (m) => m.name.trim().toLowerCase() === name.toLowerCase(),
        );
        if (exact?.muscleGroups?.length) {
          update(index, { muscleGroups: [...exact.muscleGroups] });
        }
      } catch {
        /* ignore */
      }
    }
  }

  return (
    <div class={`space-y-3${drag.isDragging() ? " is-list-dragging" : ""}`}>
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold text-ink">{t("EXERCISES")}</h3>
        <button type="button" class="btn-ghost text-xs py-1.5" onClick={add}>
          + {t("ADD_EXERCISE")}
        </button>
      </div>

      <div class="drag-list" ref={drag.setListRef}>
        <div
          class="drag-insert-line"
          ref={drag.setIndicatorRef}
          aria-hidden="true"
        />
        <div class="space-y-3">
        <Index each={props.exercises}>
          {(ex, i) => (
            <div
              data-drag-index={String(i)}
              class={drag.itemClass(i)}
              ref={(el) => drag.setItemRef(i, el)}
            >
              <div class="space-y-2 rounded-[12px] bg-[var(--surface)] p-3">
                <div class="flex items-start gap-1.5">
                  <DragHandle {...drag.handleProps(i)} />
                  <ExerciseNameLookup
                    value={ex().name}
                    icon={ex().icon}
                    type={rowType(ex())}
                    placeholder={t("EXERCISE")}
                    onChange={(name) =>
                      update(i, { name, catalogExerciseId: null })
                    }
                    onPick={(item) => {
                      update(i, catalogPatch(item, props.exercises[i]!));
                      void loadHistory(i);
                    }}
                    onBlurName={() => void loadHistory(i)}
                  />
                  <button
                    type="button"
                    class="btn-ghost px-2 text-ink-muted"
                    onClick={() => remove(i)}
                    aria-label={t("DELETE")}
                  >
                    ✕
                  </button>
                </div>

                <MuscleGroupPicker
                  value={ex().muscleGroups ?? []}
                  onChange={(next) => update(i, { muscleGroups: next })}
                />

                <ActivityFields
                  ex={ex()}
                  showErrors={props.showErrors}
                  onPatch={(patch) => update(i, patch)}
                />

                <Show when={hintOf(ex())}>
                  {(hint) => <p class="ex-last-time">{hint()}</p>}
                </Show>

                <div>
                  <textarea
                    class="input min-h-[56px] resize-y text-sm"
                    rows={2}
                    placeholder={t("EXERCISE_NOTES_PLACEHOLDER")}
                    value={ex().notes ?? ""}
                    onInput={(e) =>
                      update(i, {
                        notes:
                          e.currentTarget.value === ""
                            ? null
                            : e.currentTarget.value,
                      })
                    }
                  />
                </div>

                <Show when={historyFor() === i && rowType(ex()) === "strength"}>
                  <div class="rounded-lg bg-surface-overlay/60 px-2.5 py-2 text-xs">
                    <div class="mb-1 font-medium text-ink-muted">
                      {t("PREVIOUS_RESULTS")}
                    </div>
                    <Show when={historyLoading()}>
                      <div class="text-ink-muted">{t("LOADING")}</div>
                    </Show>
                    <Show when={!historyLoading() && history().length === 0}>
                      <div class="text-ink-muted">{t("NO_HISTORY")}</div>
                    </Show>
                    <ul class="space-y-1">
                      <Index each={history()}>
                        {(h) => (
                          <li class="flex justify-between text-ink">
                            <span>
                              {h().sets}×{h().reps}
                              {h().weight != null
                                ? ` @ ${h().weight} ${t("KG")}`
                                : ""}
                            </span>
                            <span class="text-ink-muted">
                              {new Date(h().performedAt).toLocaleDateString()}
                            </span>
                          </li>
                        )}
                      </Index>
                    </ul>
                  </div>
                </Show>
              </div>
            </div>
          )}
        </Index>
        </div>
      </div>
    </div>
  );
}
